import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import Modal from "components/common/Modal";
import ColorSelector from "components/common/ColorSelector";
import DropIcon from "components/icons/DropIcon";
import Button from "components/common/Button";
import Colors from "assets/Colors";
import Input from "components/common/Input";
import ItemRow from "components/common/ListWithMoreOptions/ItemRow";
import useCategoryMutations from "graphql/useCategoryMutations";
import AddSubCategoryModal from "components/common/AddSubCategoryModal";
import useSubcategoryMutations from "graphql/useSubcategoryMutations";

import useScreenSize from "hooks/useScreenSize";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import FormHeader from "components/common/FormHeader";
import s from "./styles";

function AddItem({ closeModal, editItem = {}, onUpdateSuccess, onAddSuccess }) {
  const { t } = useTranslation();
  const [addSubcategoryPopUp, setAddSubcategoryPopUp] = useState(false);
  const [subCategoryToEdit, setSubCategoryToEdit] = useState({});

  const { width } = useScreenSize();

  const { addCategory, updateCategory } = useCategoryMutations();
  const { addSubcategory, deleteSubcategory } = useSubcategoryMutations();
  const [newSubcategories, setNewSubcategories] = useState([]);
  const [dbLoading, setDbLoading] = useState(false);
  const loading = dbLoading;

  const { setErrorMessage } = useMessaging();

  const { handleSubmit, handleChange, values, handleReset, isValid, dirty } =
    useFormik({
      initialValues: {
        name: "",
        color: null,
        subcategories: [],
        ...editItem,
      },
      onSubmit: (values) => {
        const newValues = {
          ...values,
          creationDatetime: values.creationDatetime || new Date(),
          updateDatetime: new Date(),
          subcategories: undefined,
          __typename: undefined,
          assignedItems: undefined,
        };
        if (editItem && Object.keys(editItem).length > 0) {
          setDbLoading(true);
          updateCategory(editItem, newValues)
            .then(() => {
              handleClose();
              setDbLoading(false);
              onUpdateSuccess();
            })
            .catch((e) => {
              setDbLoading(false);
              setErrorMessage({ error: e });
            });
        } else {
          setDbLoading(true);
          addCategory(newValues)
            .then((data) => {
              if (newSubcategories.length > 0) {
                setDbLoading(false);

                const promises = newSubcategories.map((subCategory) => {
                  subCategory.categoryId = data._id;
                  return addSubcategory(subCategory);
                });
                Promise.all(promises)
                  .then(() => {
                    handleClose();
                    setDbLoading(false);
                    onAddSuccess();
                  })
                  .catch((e) => {
                    setDbLoading(false);
                    setErrorMessage({ error: e });
                  });
              }
              handleClose();
              setDbLoading(false);
              onAddSuccess();
            })
            .catch((e) => {
              setDbLoading(false);
              setErrorMessage({ error: e });
            });
        }
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Required"),
      }),
    });

  useEffect(() => {
    if (editItem && editItem.subcategories) {
      handleChange({
        target: {
          name: "subcategories",
          value: [...editItem.subcategories],
        },
      });
    }
  }, [editItem, handleChange]);

  function handleClose() {
    handleReset();
    closeModal();
  }

  const RightElements = (
    <>
      <s.Title style={{ marginBottom: "19px" }}>
        {t("Category.fields.color.label")}
      </s.Title>

      <s.ColorWrapper color={values.color}>
        <DropIcon color={values.color ? undefined : Colors.GREY} />
      </s.ColorWrapper>
      <ColorSelector
        handleChange={(value) => {
          handleChange({
            target: {
              name: "color",
              value,
            },
          });
        }}
      />
    </>
  );

  return (
    <>
      <FormHeader
        addTitle={t("components.InventoryCategories.AddItem.addCategory")}
        updateTitle={t("components.InventoryCategories.AddItem.editCategory")}
        closeModal={closeModal}
        updating={editItem && Object.keys(editItem).length > 0}
        onSave={handleSubmit}
        saving={loading}
        enabled={isValid && dirty}
      />

      <s.BodyContainer>
        <s.LeftContainer>
          <s.Title>{t("Category.newCategory")}</s.Title>
          <s.Line style={{ marginTop: "18px", marginBottom: "10px" }} />
          <s.SubTitle style={{ marginBottom: "8px" }}>
            {t("Category.fields.name.label")}
          </s.SubTitle>
          <Input
            style={{
              marginBottom: "23px",
            }}
            placeholder={t("Category.fields.name.placeholder")}
            name="name"
            onChange={handleChange}
            value={values.name}
          />

          <s.RightContainerOnBottom>{RightElements}</s.RightContainerOnBottom>

          <s.TitleAndButtonContainer style={{ marginTop: "20px" }}>
            <s.Title>{t("Category.sugCategoryAssigned")}</s.Title>
            <Button
              type="primary"
              style={{
                background: Colors.background_secondary,
                color: Colors.text_primary,
                width: "none",
                fontWeight: "500",
                border: "solid 1px",
                borderRadius: "5px",
                height: "40px",
                marginBottom: "7px",
                textShadow: "none",
                marginRight: "13px",
                marginTop: width < 768 ? "10px" : "0px",
              }}
              onClick={() => setAddSubcategoryPopUp(true)}
            >
              {t("Category.newSubcategory")}
            </Button>
          </s.TitleAndButtonContainer>
          <s.Line style={{ marginTop: "6px", marginBottom: "24px" }} />
          {values.subcategories.length > 0 && (
            <s.Line style={{ background: `${Colors.primary}10` }} />
          )}
          {newSubcategories.length < 0 &&
            values.subcategories.map((sub) => (
              <ItemRow
                keyId={sub?._id}
                key={`sub${sub?._id}`}
                columns={[sub?.name, sub?.assignedItems]}
                grid="44fr 42fr 14fr"
                handleDeleteItem={() => {
                  if (values._id) {
                    deleteSubcategory(sub);
                  } else {
                    handleChange({
                      target: {
                        name: "subcategories",
                        value: values.subcategories.filter((s) => s !== sub),
                      },
                    });
                  }
                }}
                handleEditItem={() => {
                  setSubCategoryToEdit(sub);
                  setAddSubcategoryPopUp(true);
                }}
                labelDelete={t("Category.deleteSubCategory")}
                labelEdit={t("Category.editSubCategory")}
              />
            ))}
          {values.subcategories.map((sub) => (
            <ItemRow
              keyId={sub?._id}
              key={`sub${sub?._id}`}
              columns={[sub?.name, sub?.assignedItems]}
              grid="44fr 42fr 14fr"
              handleDeleteItem={() => {
                if (values._id) {
                  deleteSubcategory(sub);
                } else {
                  handleChange({
                    target: {
                      name: "subcategories",
                      value: values.subcategories.filter((s) => s !== sub),
                    },
                  });
                }
              }}
              handleEditItem={() => {
                setSubCategoryToEdit(sub);
                setAddSubcategoryPopUp(true);
              }}
              labelDelete={t("Category.deleteSubCategory")}
              labelEdit={t("Category.editSubCategory")}
            />
          ))}
          {newSubcategories.map((sub) => (
            <ItemRow
              keyId={sub?._id}
              key={`sub${sub?._id}`}
              columns={[sub?.name, sub?.assignedItems]}
              grid="44fr 42fr 14fr"
              handleDeleteItem={() => {
                setNewSubcategories(
                  newSubcategories.filter((c) => c._id !== sub?._id)
                );
              }}
              handleEditItem={() => {
                setSubCategoryToEdit(sub);
                setAddSubcategoryPopUp(true);
              }}
              labelDelete={t("Category.deleteSubCategory")}
              labelEdit={t("Category.editSubCategory")}
            />
          ))}
        </s.LeftContainer>
        <s.RightContainer>{RightElements}</s.RightContainer>

        <Modal
          onCancel={() => {
            setSubCategoryToEdit({});
            setAddSubcategoryPopUp(false);
          }}
          visible={addSubcategoryPopUp}
        >
          {addSubcategoryPopUp && (
            <AddSubCategoryModal
              close={() => {
                if (subCategoryToEdit) {
                  setSubCategoryToEdit({});
                }
                setAddSubcategoryPopUp(false);
              }}
              setNewSubcategories={setNewSubcategories}
              newSubcategories={newSubcategories}
              setNewSubCategory={(subCatValues) => {
                if (!values._id) {
                  const newSubcategories =
                    Object.keys(subCategoryToEdit).length > 0
                      ? values.subcategories.map((sub) => {
                          if (sub === subCategoryToEdit) {
                            return subCatValues;
                          }
                          return sub;
                        })
                      : [...values.subcategories, subCatValues];

                  handleChange({
                    target: {
                      name: "subcategories",
                      value: newSubcategories,
                    },
                  });
                }
              }}
              parentCategory={{
                name: values.name,
                id: values._id,
              }}
              editItem={subCategoryToEdit}
            />
          )}
        </Modal>
      </s.BodyContainer>
    </>
  );
}

export default AddItem;
